import { useRef, useEffect } from 'react';

import { Coordinates } from '../typings/main';

interface UseGoogleMapProps {
  mapRef: React.RefObject<HTMLDivElement>;
  center: Coordinates | null;
  googleAPILoaded: boolean;
  isInitializing: boolean;
  mapStyles?: google.maps.MapTypeStyle[];
  onMapReady?: (map: google.maps.Map) => void;
}

export const useGoogleMap = ({
  mapRef,
  center,
  googleAPILoaded,
  isInitializing,
  mapStyles = [],
  onMapReady
}: UseGoogleMapProps) => {
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const lastCenterRef = useRef<Coordinates | null>(null); // track last center

  useEffect(() => {
    if (!googleAPILoaded || !mapRef.current || !center || isInitializing) return;

    if (mapInstanceRef.current) return;

    try {
      const map = new google.maps.Map(mapRef.current, {
        zoom: 10,
        center: center,
        styles: mapStyles,
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      });

      mapInstanceRef.current = map;
      lastCenterRef.current = center;

      if (onMapReady) onMapReady(map);
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }, [googleAPILoaded, isInitializing, mapRef, onMapReady]);

  // Update map center when it changes
  useEffect(() => {
    if (!mapInstanceRef.current || !center) return;

    const hasChanged =
      !lastCenterRef.current ||
      lastCenterRef.current.lat !== center.lat ||
      lastCenterRef.current.lng !== center.lng;

    if (hasChanged) {
      mapInstanceRef.current.setCenter(center);
      lastCenterRef.current = center;
    }
  }, [center]);

  return mapInstanceRef.current;
};